import React from "react";
import PropTypes from "prop-types";
import {
    formatRecurringPeriod,
    formatIntroPeriod
} from "common/utils/subscriptionCopyHelpers";
import { to2DecimalPriceString } from "common/utils/NumberFormatter";
import { SUBSCRIPTION_PERIODS } from "common/constants/Constants";
import * as THEMES from "./themes";
import styles from "./styles.module.scss";

const {
    YEAR,
    QUARTER,
    MONTH,
    WEEK,
    SIX_MONTHS,
    TWO_YEARS,
    THREE_YEARS,
    FOUR_YEARS,
    FIVE_YEARS
} = SUBSCRIPTION_PERIODS;

// startDate is used to identify rateplans that will start in the future.
// Currently it is only used by save offers
const DiscountDisclaimer = ({ pricing, isStudentOffer, isBulkSubscription, bulkSubscriptionDetails, startDate, theme }) => {
    if (isBulkSubscription) {
        const {
            productName,
            numberOfUsers,
            pricePerUnit,
            bulkSubBillingPeriod
        } = bulkSubscriptionDetails;

        const users = numberOfUsers ? `${ numberOfUsers } user${ numberOfUsers === "1" ? "" : "s" }` : "user";
        const periodWord = bulkSubBillingPeriod === "Annual"
            ? "annually"
            : "monthly";

        return (
            <div className={ styles.discountDisclaimer }>
                { productName }: ${ pricePerUnit } { periodWord } x { users }.<br />
                Tax Included.
            </div>
        );
    }

    const {
        hasDiscount,
        hasSurcharge,
        total,
        totalWithDiscount,
        billingPeriod,
        discountLength
    } = pricing;
    const price = to2DecimalPriceString(total);
    const priceWithDiscount = to2DecimalPriceString(totalWithDiscount);
    const period = billingPeriod.toLowerCase();
    const futureBillPrefix = startDate && (
        <>
            Your updated subscription will take effect on{ " " }
            <span className={ styles.highlighted }>
                { startDate }
            </span>
            . You will be billed{ " " }
        </>
    );
    const todayBillSuffix = !startDate && " charged today";

    // We don't have a recurring price in student rate plan, so we have to hardcode it to $34.99 per month
    // even for student annual plan
    if (isStudentOffer) {
        return (
            <div className={ styles[theme] }>
                { futureBillPrefix }
                <span className={ styles.highlighted } selectid="intro-price">{ price }</span>
                { todayBillSuffix } and
                <span className={ styles.highlighted }> { formatRecurringPeriod(billingPeriod) } </span>
                until you graduate. Once you are no longer a student, you will be automatically charged
                <span className={ styles.highlighted } selectid="full-price"> $34.99 every month</span>. Tax Included.
            </div>
        );
    }

    if (!hasDiscount && !hasSurcharge) {
        return (
            <div className={ styles[theme] }>
                { futureBillPrefix }
                <span className={ styles.highlighted } selectid="intro-price">{ price }</span>
                { todayBillSuffix }. You will automatically be charged
                <span className={ styles.highlighted } selectid="full-price"> { price } { formatRecurringPeriod(billingPeriod) }</span>
                . Tax Included.
            </div>
        );
    }

    /* eslint-disable no-fallthrough */
    // Below if condition is added to accommodate one month and free intro offer
    switch (period) {
        case QUARTER:
        case MONTH:
        case WEEK:
        case YEAR:
            if (discountLength !== 1 && totalWithDiscount > 0) {
                const userFriendlyPeriod = period === YEAR ? "year" : period;

                return (
                    <div className={ styles[theme] }>
                        { futureBillPrefix }
                        <span className={ styles.highlighted } selectid="intro-price">{ priceWithDiscount }</span>
                        { todayBillSuffix } and every { userFriendlyPeriod } of your
                        <span className={ styles.highlighted } selectid="intro-length"> { formatIntroPeriod(pricing) } </span>
                        introductory period. After your introductory period, you will be automatically charged
                        <span className={ styles.highlighted } selectid="full-price"> { price } every { userFriendlyPeriod }</span>
                        . Tax Included.
                    </div>
                );
            }
        case SIX_MONTHS:
        case TWO_YEARS:
        case THREE_YEARS:
        case FOUR_YEARS:
        case FIVE_YEARS:
        default:
            return (
                <div className={ styles[theme] }>
                    { futureBillPrefix }
                    <span className={ styles.highlighted } selectid="intro-price">{ priceWithDiscount }</span>
                    { todayBillSuffix }. { hasSurcharge ? "After your" : "At the end of your" }
                    <span className={ !hasSurcharge && styles.highlighted } selectid="intro-length"> { formatIntroPeriod(pricing, hasSurcharge) }</span>
                    { hasSurcharge ? "" : " introductory period" }, you will be automatically charged
                    <span className={ styles.highlighted } selectid="full-price"> { price } { formatRecurringPeriod(billingPeriod) }</span>
                    . Tax Included.
                </div>
            );
    }
    /* eslint-enable no-fallthrough */
};

DiscountDisclaimer.defaultProps = {
    isStudentOffer: false,
    theme: THEMES.DEFAULT
};

DiscountDisclaimer.propTypes = {
    pricing: PropTypes.shape({
        total: PropTypes.number,
        billingPeriod: PropTypes.string.isRequired,
        discountPeriod: PropTypes.oneOf(["month", "semi_annual", "annual", "week", "quarter"]),
        discountLength: PropTypes.number,
        className: PropTypes.string
    }).isRequired,
    isStudentOffer: PropTypes.bool,
    startDate: PropTypes.string,
    theme: PropTypes.string
};

export default DiscountDisclaimer;

export const DISCOUNT_DISCLAIMER_THEMES = THEMES;
